/**
 * Performance Score Calculator
 *
 * Calculates a comprehensive performance score that combines multiple dimensions:
 * - Commit complexity score (technical contribution quality)
 * - Pull request count (delivery throughput)
 * - Code changes (lines added/removed)
 * - Issue resolution (problem-solving)
 * - Commit type distribution (work variety)
 *
 * The score is normalized to a 0-100 scale for easy interpretation.
 */

export interface PerformanceMetrics {
  // Commit metrics
  commitScore: number // Sum of complexity scores
  commitCount: number // Number of commits
  linesAdded: number // Total insertions
  linesRemoved: number // Total deletions
  filesChanged: number // Total files modified

  // PR metrics
  prCount: number // Merged PRs
  prAdditions: number // Lines added via PRs
  prDeletions: number // Lines removed via PRs

  // Issue metrics
  issuesResolved: number // Issues closed

  // Commit type distribution
  featureCommits: number
  bugfixCommits: number
  refactoringCommits: number
  otherCommits: number
}

export interface PerformanceScore {
  overall: number // 0-100 overall performance score
  breakdown: {
    quality: number // Code quality (complexity score)
    throughput: number // Delivery speed (PRs, commits)
    impact: number // Code impact (lines changed)
    problemSolving: number // Issue resolution
    versatility: number // Work variety
  }
  metrics: PerformanceMetrics
}

/**
 * Calculate a comprehensive performance score
 *
 * @param metrics - Raw performance metrics
 * @param timeRangeDays - Optional number of days in the time range for normalization (defaults to 30)
 * @returns Performance score with overall score and breakdown
 */
export function calculatePerformanceScore(
  metrics: PerformanceMetrics,
  timeRangeDays = 30,
): PerformanceScore {
  // Calculate individual component scores (0-100 scale)
  // All thresholds are normalized to a 30-day baseline, then adjusted for actual time range

  // Normalize factor: if time range is 7 days, thresholds should be 7/30 of the baseline
  const normalizeFactor = timeRangeDays / 30

  // 1. Quality Score (30% weight) - Based on commit complexity scores
  // Higher complexity scores indicate more challenging/valuable contributions
  const avgComplexityScore = metrics.commitCount > 0 ? metrics.commitScore / metrics.commitCount : 0
  const qualityScore = Math.min(100, (avgComplexityScore / 50) * 100) // Normalize around 50 points avg

  // 2. Throughput Score (25% weight) - Based on merged PRs and commit frequency
  // Measures delivery speed and consistency (normalized to time range)
  const prThreshold = 10 * normalizeFactor // 10 PRs per 30 days baseline
  const commitThreshold = 50 * normalizeFactor // 50 commits per 30 days baseline
  const prScore = Math.min(100, (metrics.prCount / prThreshold) * 100)
  const commitFrequencyScore = Math.min(100, (metrics.commitCount / commitThreshold) * 100)
  const throughputScore = prScore * 0.6 + commitFrequencyScore * 0.4

  // 3. Impact Score (20% weight) - Based on lines of code changed
  // Measures the scale of contribution (normalized to time range)
  const totalLinesChanged = metrics.linesAdded + metrics.linesRemoved
  const linesThreshold = 1000 * normalizeFactor // 1000 lines per 30 days baseline
  const impactScore = Math.min(100, (totalLinesChanged / linesThreshold) * 100)

  // 4. Problem Solving Score (15% weight) - Based on issues resolved
  // Measures proactive problem-solving (normalized to time range)
  const issuesThreshold = 5 * normalizeFactor // 5 issues per 30 days baseline
  const problemSolvingScore = Math.min(100, (metrics.issuesResolved / issuesThreshold) * 100)

  // 5. Versatility Score (10% weight) - Based on commit type distribution
  // Measures breadth of contribution types
  const totalTypedCommits =
    metrics.featureCommits +
    metrics.bugfixCommits +
    metrics.refactoringCommits +
    metrics.otherCommits
  if (totalTypedCommits === 0) {
    // If no typed commits, use commit count as baseline
    const versatilityScore = metrics.commitCount > 0 ? 50 : 0

    const overall =
      qualityScore * 0.3 +
      throughputScore * 0.25 +
      impactScore * 0.2 +
      problemSolvingScore * 0.15 +
      versatilityScore * 0.1

    return {
      overall: Math.round(overall * 10) / 10, // Round to 1 decimal
      breakdown: {
        quality: Math.round(qualityScore * 10) / 10,
        throughput: Math.round(throughputScore * 10) / 10,
        impact: Math.round(impactScore * 10) / 10,
        problemSolving: Math.round(problemSolvingScore * 10) / 10,
        versatility: Math.round(versatilityScore * 10) / 10,
      },
      metrics,
    }
  }

  // Calculate Shannon diversity index for commit types
  const types = [
    metrics.featureCommits,
    metrics.bugfixCommits,
    metrics.refactoringCommits,
    metrics.otherCommits,
  ]
  const maxDiversity = Math.log(4) // Log of 4 types

  let diversity = 0
  for (const count of types) {
    if (count > 0) {
      const proportion = count / totalTypedCommits
      diversity -= proportion * Math.log(proportion)
    }
  }

  // Normalize diversity to 0-100 scale
  const versatilityScore = maxDiversity > 0 ? (diversity / maxDiversity) * 100 : 0

  // Calculate weighted overall score
  const overall =
    qualityScore * 0.3 +
    throughputScore * 0.25 +
    impactScore * 0.2 +
    problemSolvingScore * 0.15 +
    versatilityScore * 0.1

  return {
    overall: Math.round(overall * 10) / 10, // Round to 1 decimal
    breakdown: {
      quality: Math.round(qualityScore * 10) / 10,
      throughput: Math.round(throughputScore * 10) / 10,
      impact: Math.round(impactScore * 10) / 10,
      problemSolving: Math.round(problemSolvingScore * 10) / 10,
      versatility: Math.round(versatilityScore * 10) / 10,
    },
    metrics,
  }
}

/**
 * Calculate performance metrics from raw data
 *
 * @param commits - Array of commits
 * @param pullRequests - Array of pull requests (merged)
 * @param issues - Array of issues (resolved)
 * @returns Performance metrics
 */
export function calculateMetrics(
  commits: Array<{
    score: number
    insertions: number
    deletions: number
    filesChanged: number
    commitType?: string
  }>,
  pullRequests: Array<{
    additions: number
    deletions: number
    state: string
  }>,
  issues: Array<{
    state: string
  }>,
): PerformanceMetrics {
  // Calculate commit metrics
  const commitScore = commits.reduce((sum, c) => sum + (c.score || 0), 0)
  const commitCount = commits.length
  const linesAdded = commits.reduce((sum, c) => sum + (c.insertions || 0), 0)
  const linesRemoved = commits.reduce((sum, c) => sum + (c.deletions || 0), 0)
  const filesChanged = commits.reduce((sum, c) => sum + (c.filesChanged || 0), 0)

  // Calculate PR metrics (only merged PRs)
  const mergedPRs = pullRequests.filter((pr) => pr.state === "merged")
  const prCount = mergedPRs.length
  const prAdditions = mergedPRs.reduce((sum, pr) => sum + (pr.additions || 0), 0)
  const prDeletions = mergedPRs.reduce((sum, pr) => sum + (pr.deletions || 0), 0)

  // Calculate issue metrics (only closed issues)
  const issuesResolved = issues.filter((issue) => issue.state === "closed").length

  // Calculate commit type distribution
  const featureCommits = commits.filter((c) => c.commitType === "feature").length
  const bugfixCommits = commits.filter((c) => c.commitType === "bugfix").length
  const refactoringCommits = commits.filter((c) => c.commitType === "refactoring").length
  const otherCommits = commits.filter(
    (c) =>
      c.commitType &&
      c.commitType !== "feature" &&
      c.commitType !== "bugfix" &&
      c.commitType !== "refactoring",
  ).length

  return {
    commitScore,
    commitCount,
    linesAdded,
    linesRemoved,
    filesChanged,
    prCount,
    prAdditions,
    prDeletions,
    issuesResolved,
    featureCommits,
    bugfixCommits,
    refactoringCommits,
    otherCommits,
  }
}
